import { useHotkeys } from 'react-hotkeys-hook';
import { useCallback, useMemo } from 'react';
import { useKeyboardShortcutsContext } from '../contexts/KeyboardShortcutsContext';

export interface KeyboardShortcutOptions {
  /** Whether the shortcut should be enabled */
  enabled?: boolean;
  /** Whether to prevent default browser behavior */
  preventDefault?: boolean;
  /** Whether to stop event propagation */
  stopPropagation?: boolean;
  /** Description for help modal */
  description?: string;
  /** Category for help modal */
  category?: string;
}

/**
 * Custom hook wrapper around react-hotkeys-hook that automatically
 * prevents shortcuts when user is typing in inputs/textareas
 */
export const useKeyboardShortcut = (
  keys: string,
  callback: (event: KeyboardEvent) => void,
  options: KeyboardShortcutOptions = {}
) => {
  const {
    enabled = true,
    preventDefault = true,
    stopPropagation = false,
    description,
    category
  } = options;

  // Get context for sequence tracking
  const context = useKeyboardShortcutsContext();
  const { addSequenceKey, clearSequence } = context || {};

  // Check if this is a sequential shortcut (contains commas or multiple keys)
  const isSequential = keys.includes(',') || keys.includes(' then ');

  // Check if user is currently typing in an input field
  const isTypingInInput = useCallback(() => {
    const activeElement = document.activeElement;
    
    if (!activeElement) return false;

    const tagName = activeElement.tagName.toLowerCase();
    const isInput = ['input', 'textarea', 'select'].includes(tagName);
    const isContentEditable = activeElement.getAttribute('contenteditable') === 'true';
    
    // Check for Quill editor
    const isQuillEditor = activeElement.closest('.ql-editor') !== null;
    
    // Check for other rich text editors
    const isRichTextEditor = activeElement.closest('[contenteditable="true"]') !== null;
    
    return isInput || isContentEditable || isQuillEditor || isRichTextEditor;
  }, []);

  // Enhanced callback that checks if user is typing
  const enhancedCallback = useCallback((event: KeyboardEvent) => {
    // Don't trigger if user is typing in an input
    if (isTypingInInput()) {
      return;
    }

    // Track sequence if this is a sequential shortcut
    if (isSequential && addSequenceKey) {
      // Get the pressed key from the event
      const key = event.key;
      addSequenceKey(key);
      
      // If this is the last key in the sequence, clear after callback
      setTimeout(() => {
        clearSequence?.();
      }, 500);
    }

    // Call the original callback
    callback(event);

    // Handle preventDefault and stopPropagation
    if (preventDefault) {
      event.preventDefault();
    }
    
    if (stopPropagation) {
      event.stopPropagation();
    }
  }, [callback, isTypingInInput, preventDefault, stopPropagation, isSequential, addSequenceKey, clearSequence]);

  // Use react-hotkeys-hook with our enhanced callback
  useHotkeys(
    keys,
    enhancedCallback,
    {
      enabled,
      preventDefault: true, // Always prevent default browser behavior
      enableOnFormTags: false, // We handle input detection manually
    }
  );

  // Return metadata for help modal
  return useMemo(() => ({
    keys,
    description,
    category,
    enabled
  }), [keys, description, category, enabled]);
};

/**
 * Hook for global shortcuts that should work everywhere
 * (bypasses input detection)
 */
export const useGlobalKeyboardShortcut = (
  keys: string,
  callback: (event: KeyboardEvent) => void,
  options: Omit<KeyboardShortcutOptions, 'enabled'> = {}
) => {
  const {
    preventDefault = true,
    stopPropagation = false,
    description,
    category
  } = options;

  const enhancedCallback = useCallback((event: KeyboardEvent) => {
    callback(event);

    if (preventDefault) {
      event.preventDefault();
    }
    
    if (stopPropagation) {
      event.stopPropagation();
    }
  }, [callback, preventDefault, stopPropagation]);

  useHotkeys(
    keys,
    enhancedCallback,
    {
      enabled: true,
      preventDefault: true, // Always prevent default browser behavior
      enableOnFormTags: true, // Allow on form elements for global shortcuts
    }
  );

  return useMemo(() => ({
    keys,
    description,
    category,
    enabled: true
  }), [keys, description, category]);
};
